import type {
  CodeableConcept,
  Practitioner,
  Reference,
  Task,
} from '@medplum/fhirtypes'
import { PRACTITIONER_ROLE } from '#src/codeSystems.ts'
import type { IDTRole } from '#src/models/care-teams/constants.ts'
import {
  SIGNATURE_TASK_CODES,
  SignatureTaskCode,
} from '#src/models/tasks/constants.ts'
import { SignatureTask } from '#src/models/tasks/signature/signatureTask.ts'
import type { QueryTypes } from '#src/search.ts'

interface SDRSignatureTaskParams {
  sdr: Reference<Task>
  requester: Reference<Practitioner>
  // The IDT seat this signature is for.
  role?: IDTRole
  // Omitted when the seat is empty.
  signer?: Reference<Practitioner>
}

export class SDRSignatureTask extends SignatureTask {
  constructor({ sdr, requester, signer, role }: SDRSignatureTaskParams) {
    super({
      code: SDRSignatureTask.code,
      focus: sdr,
      owner: signer,
      requester,
      roles: role ? [role] : undefined,
    })
  }

  static override get code(): CodeableConcept {
    return SIGNATURE_TASK_CODES[SignatureTaskCode.SDR]
  }

  static bySDR(sdr: Reference<Task>): QueryTypes {
    return {
      code: SIGNATURE_TASK_CODES[SignatureTaskCode.SDR],
      focus: sdr,
    }
  }

  // SDR signatures are one per IDT seat, keyed on performerType like care
  // plan signatures.
  static byRoles({
    sdr,
    role,
  }: {
    sdr: Reference<Task>
    role: IDTRole
  }): QueryTypes {
    return {
      ...this.bySDR(sdr),
      'status:not': ['cancelled', 'entered-in-error'],
      performer: `${PRACTITIONER_ROLE}|${role}`,
    }
  }

  static queryOpenBySDR(sdr: Reference<Task>): QueryTypes {
    return {
      ...this.bySDR(sdr),
      'status:not': ['completed', 'cancelled', 'entered-in-error'],
    }
  }

  static searchQuery(): QueryTypes {
    return {
      code: this.code,
    }
  }
}
